import { Popover } from "@mui/material";
import { TreeNode, useOrganizationTreeStore } from "../../../../state-management/useOrganizationTreeStore";
import { SubmitHandler, useForm } from "react-hook-form";
import Label from "../../../../components/form_fields/Label";
import { JobTitleDto } from "../../../../models/job-titles.dto";
import { useUpdateCompanyTree } from "../../../../services/company-tree-node.service";
import { useState } from "react";
import API from '../../../../services/utils/api';
import useCompanyStore from "../../../../state-management/useCompanyStore";

type FormInputs = {
    jobTitleId: number;
}

type UpdateJobTitleModalProps = {
    anchorEl: any;
    selectedNode: TreeNode;
    jobTitles: JobTitleDto[];
    handleClose: (closeMenu?: any) => void;
}

const UpdateJobTitleModal = ({
    anchorEl,
    selectedNode,
    jobTitles,
    handleClose,
}: UpdateJobTitleModalProps) => {
	const { currentCompany } = useCompanyStore();
    const { generateTree } = useOrganizationTreeStore();
    const { mutate: updateTreeNode } = useUpdateCompanyTree();
    const [uploading, setUploading] = useState(false);
    
	const {
		register,
		handleSubmit,
		formState: { errors },
	} = useForm<FormInputs>({
        defaultValues: {
            jobTitleId: selectedNode.data.jobTitleId || undefined
        }
    });

    const onSubmit: SubmitHandler<FormInputs> = (data) => {
        setUploading(true);

        updateTreeNode(
            { 
                companyId: currentCompany?.id as number,
                nodeId: selectedNode.id,
                payload: { jobTitleId: data.jobTitleId }
            },
            {
                onSuccess: async () => {
                    const { data } = await API.HrAPI.getCompanyTree(currentCompany?.id as number);
                    generateTree(data);
                    handleClose("true");
                    setUploading(false);
                },
                onError: () => setUploading(false)
            }
        )
    };
    
    return (
        <Popover
            open={Boolean(anchorEl)}
            anchorEl={anchorEl}
            onClose={handleClose}
            anchorOrigin={{
                vertical: "top",
                horizontal: "right",
            }}
            transformOrigin={{
                vertical: "top",
                horizontal: "left",
            }}
            PaperProps={{
                style: {
                    width: "330px",
                    borderRadius: 8,
                    display: "flex",
                    justifyContent: "center",
                    boxShadow: "-10px -5px 30px 0px rgba(0, 0, 0, 0.15)",
                },
            }}
        >
            <form 
                onSubmit={handleSubmit(onSubmit)}
                className="w-full p-4 flex flex-col gap-4 text-sm"
            >
                <div className="w-full">
                    <Label title="Job Title" for="jobTitleId" />
                    <select
                        className={`w-full bg-secondary-gray rounded-lg h-14 px-5 mt-2 text-sm  ${
                            errors.jobTitleId
                                ? " border border-red-500 focus:border-red-500 focus:outline-red-500"
                                : "border-none"
                        }`}
                        {...register("jobTitleId", {
                            required: "Select job title here",
                            valueAsNumber: true,
                        })}
                    >
                        <option value="">
                            Select Job Title
                        </option>
                        {jobTitles.map((jobTitle) => (
                            <option 
                                key={jobTitle.id} 
                                value={jobTitle.id} 
                            >
                                {jobTitle.name}
                            </option>
                        ))}
                    </select>
                    {errors.jobTitleId && (
                        <p className="text-red-500 text-sm mt-1">
                            {errors.jobTitleId.message}
                        </p>
                    )}
                </div>

                <button 
                    type="submit" 
                    disabled={uploading}
                    className={`w-full bg-primary-mango py-3 rounded-lg text-white text-2xs
                        capitalize node-button ${uploading ? "opacity-50" : "hover:shadow-sm"}
                    `}
                >
                    {uploading ? "Saving..." : "Save"}
                </button>
            </form>
        </Popover>
    );
}
 
export default UpdateJobTitleModal;